import { useState, useCallback } from "react";
import { FiMail, FiCheck, FiX, FiAlertCircle, FiXCircle } from "react-icons/fi";
import { cn } from "utils/helpers";
import { useAcceptInvitation } from "../../../../../hooks/usePanelScoringQueries";
import { Rfx } from "newui/features/Rfx/types/rfx.types";
import { DeclineDrawer } from "./DeclineDrawer";
import { DisclaimerModal } from "newui/features/Rfx/Evaluation/components/DisclaimerModal";
import { PanelMemberStatus } from "newui/features/Rfx/Evaluation/types/panel.type";
import { ConflictPendingReviewNotice } from "newui/features/Rfx/Evaluation/components/ConflictPendingReviewNotice";
import { EVALUATION_STEP_NUMBERS } from "newui/features/Rfx/Evaluation/constants/evaluation.constants";

interface InvitationBannerProps {
	currentStatus: PanelMemberStatus;
	currentStep: number;
	disclaimerRequired?: boolean;
	evalId: number;
	isConflictPending: boolean;
	memberId: number;
	/**
	 * Display-only. Do NOT derive the RFx id from this — see `rfxId` below.
	 */
	rfxData: Rfx | undefined | null;
	/**
	 * TASK-028: passed in from the URL-seeded context id, never `rfxData?.id`.
	 * An external panel member 403s on the procurer-detail fetch, so `rfxData`
	 * can be null indefinitely; keying the accept/decline/disclaimer calls off it
	 * produced `/rfx/undefined/...` request URLs.
	 */
	rfxId: number | null;
	rfxTitle?: string;
	onAccept?: () => void;
	onDecline?: () => void;
}

export const InvitationBanner: React.FC<InvitationBannerProps> = ({
	currentStatus,
	currentStep,
	isConflictPending,
	rfxData,
	rfxId,
	evalId,
	memberId,
	rfxTitle,
	disclaimerRequired = false,
	onAccept,
	onDecline,
}) => {

	/**
	 * Accepted/conflict state is DERIVED from props, never mirrored into local
	 * state. It used to be `useState(currentStatus === "accepted")`, which reads
	 * the prop once on mount and then ignores it — so after the accept, decline
	 * and disclaimer mutations invalidated the panel query and fresh props
	 * arrived, this component kept rendering its stale first answer. That is what
	 * made the banner disagree with the server (showing "you have accepted" for a
	 * member the API reported as declined, and losing the conflict notice on
	 * refresh). Both mutations already invalidate, so props are the live truth.
	 */
	const accepted = currentStatus === "accepted";
	const conflictPending = isConflictPending;
	const [showDeclineDrawer, setShowDeclineDrawer] = useState(false);
	const [showDisclaimer, setShowDisclaimer] = useState(false);
	const acceptMutation = useAcceptInvitation(rfxId, evalId);

	const doAccept = useCallback(() => {
		acceptMutation.mutate(memberId, {
			onSuccess: () => {
				onAccept?.();
			},
		});
	}, [acceptMutation, memberId, onAccept]);

	// Click Accept: if disclaimer required, show modal first. Otherwise accept immediately.
	const handleAccept = useCallback(() => {
		if (disclaimerRequired) {
			setShowDisclaimer(true);
			return;
		}
		doAccept();
	}, [disclaimerRequired, doAccept]);

	/**
	 * Submitting the disclaimer is what completes the acceptance — the server
	 * accepts the member inside `submitDisclaimer`, precisely because
	 * `acceptInvitation` returns early without a status when a disclaimer is
	 * required. So there is nothing to do here but close the modal; the mutation
	 * has already invalidated the panel query and the new status arrives as props.
	 *
	 * A declared conflict still blocks scoring, but it does so via
	 * `disclaimer_status` (surfaced as `isConflictPending`), NOT by withholding
	 * acceptance.
	 */
	const handleDisclaimerComplete = useCallback((_hasConflict: boolean) => {
		setShowDisclaimer(false);
		onAccept?.();
	}, [onAccept]);

	// Disclaimer cancelled — do nothing, member stays invited
	const handleDisclaimerCancel = useCallback(() => {
		setShowDisclaimer(false);
	}, []);

	const handleDeclined = useCallback(() => {
		setShowDeclineDrawer(true);
	}, [])

	const handleOnDecline = useCallback(() => {
		onDecline?.();
	}, [onDecline])

	const declineDrawer =
		<DeclineDrawer
			isOpen={showDeclineDrawer}
			onClose={() => setShowDeclineDrawer(false)}
			rfxId={rfxId}
			evalId={evalId}
			memberId={memberId}
			rfxLeadName={rfxData?.rfxLead?.fullName}
			rfxLeadEmail={rfxData?.rfxLead?.email}
			rfxTitle={rfxData?.name}
			onDeclined={handleOnDecline}
		/>;

	const disclaimerModal =
		<DisclaimerModal
			isOpen={showDisclaimer}
			onClose={handleDisclaimerCancel}
			onSubmitSuccess={handleDisclaimerComplete}
			rfxId={rfxId}
			evalId={evalId}
			memberId={memberId}
		/>;

	// Guarded AFTER the hooks, deliberately. This was an early `return null`
	// above the `useState`/`useCallback` calls, so the number of hooks rendered
	// changed with the prop — React's rules-of-hooks violation, which corrupts
	// hook state the moment `currentStatus` goes from absent to present (exactly
	// what happens when the panel query resolves).
	if (!currentStatus) {
		return null;
	}

	if (accepted || currentStatus === "in_progress" || currentStatus === "submitted") {
		return (
			<div className="space-y-4">
				<div
					className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3"
					data-testid="invitation-accepted-banner"
				>
					<FiCheck size={18} className="shrink-0 text-green-600" aria-hidden="true" />
					<p className="text-sm text-green-700">
						You have accepted to score this Evaluation.
					</p>
					<button className="bg-red-600 rounded-md p-2 text-white hover:bg-red-800 text-sm ml-auto" onClick={handleDeclined}>
						I can no longer participate
					</button>
					{declineDrawer}
				</div>
				{conflictPending && <ConflictPendingReviewNotice rfxLead={rfxData?.rfxLead} />}
			</div>
		);
	}

	if (currentStatus === "declined") {
		return (
			<div
				className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3"
				data-testid="invitation-declined-banner"
			>
				<FiXCircle size={18} className="shrink-0 text-red-600" aria-hidden="true" />
				<p className="text-sm text-red-700">
					You have declined to participate. You cannot score this Evaluation.
				</p>
				{currentStep < EVALUATION_STEP_NUMBERS.CLARIFICATIONS && <button className="bg-blue-600 rounded-md p-2 text-white hover:bg-blue-800 text-sm" onClick={handleAccept}>
					I want to participate
				</button>}
				{disclaimerModal}
			</div>
		);
	}

	// catch all if not invited status
	if (currentStatus !== "invited") {
		return;
	}

	return (
		<div
			className="rounded-lg border border-[#0031DD]/20 bg-[#e8eeff] px-4 py-4"
			data-testid="invitation-banner"
		>
			<div className="flex items-start gap-3">
				<FiMail size={20} className="mt-0.5 shrink-0 text-[#0031DD]" aria-hidden="true" />
				<div className="flex-1">
					<p className="text-sm font-medium text-gray-900">
						You have been invited to participate in evaluating this RFx
					</p>
					{rfxTitle && (
						<p className="mt-0.5 text-xs text-gray-600">
							{rfxTitle}
						</p>
					)}
					<p className="mt-1.5 text-xs text-gray-500">
						Accept to begin scoring suppliers, or decline with a reason.
					</p>

					{(acceptMutation.isError) && (
						<p className="mt-2 inline-flex items-center gap-1 text-xs text-red-600">
							<FiAlertCircle size={12} />
							Failed to accept invitation. Please try again.
						</p>
					)}

					<div className="mt-3 flex items-center gap-2">
						<button
							type="button"
							onClick={handleAccept}
							disabled={acceptMutation.isPending}
							className={cn(
								"inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition-colors",
								"bg-[#0031DD] text-white hover:bg-[#0028b8] active:bg-[#002099]",
								"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0031DD] focus-visible:ring-offset-2",
								acceptMutation.isPending && "opacity-50 cursor-not-allowed"
							)}
							data-testid="accept-invitation-btn"
						>
							<FiCheck size={16} aria-hidden="true" />
							{acceptMutation.isPending ? "Accepting..." : "Accept"}
						</button>
						<button
							type="button"
							onClick={handleDeclined}
							disabled={acceptMutation.isPending}
							className={cn(
								"inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors",
								"hover:bg-gray-50 active:bg-gray-100",
								"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0031DD] focus-visible:ring-offset-2"
							)}
							data-testid="decline-invitation-btn"
						>
							<FiX size={16} aria-hidden="true" />
							Decline
						</button>
					</div>
				</div>
			</div>
			{declineDrawer}
			{disclaimerModal}
		</div>
	);
};
